import { ENERGY_FORECAST_VALUE_UNIT } from "@/energyPlotView/const";
import type { EnergyPlot } from "@/energyPlotView/types";
import { normalizeSiteIdForEnergyApi, remapSiteIdForForecastTypeChange } from "@/energyPlotView/utils";
import type { DropdownItem, RadioItem } from "@mm/zeus";
import { useMemo } from "react";

import { useEnergyForecastIsLoading } from "@/reducer/selectors/EnergyForecastSelector";
import { isEnergySubSiteChecked, updateEnergySubSiteIds } from "./subSiteSelection";
import {
  type AvailableValueNode,
  transformNodeToDropdownItem,
  transformNodeToDropdownItemForForecastType,
  useAvailableEnergyForecastDataTree,
} from "./useAvailableEnergyForecastDataTree";

const toCanonicalSiteId = (siteId: string, forecastType: string) => normalizeSiteIdForEnergyApi(siteId, forecastType);

const isForecastTypeAvailableForSite = (
  tree: AvailableValueNode | undefined,
  siteId: string,
  currentForecastType: string,
  nextForecastType: string,
) => {
  const forecastTypeNode = tree?.findChildrenNode(nextForecastType);
  if (!forecastTypeNode?.value) {
    return false;
  }

  const nextSiteId = remapSiteIdForForecastTypeChange(siteId, currentForecastType, nextForecastType);

  return (forecastTypeNode.children || []).some((modelNode) =>
    (modelNode.children || []).some((siteNode) => siteNode.value === nextSiteId),
  );
};

export function resolveAvailableEnergyForecastTypeNodes(
  tree: AvailableValueNode | undefined,
  siteId?: string,
  currentForecastType?: string,
) {
  const forecastTypeNodes = tree?.children || [];

  if (!siteId || !currentForecastType) {
    return forecastTypeNodes;
  }

  const availableForecastTypeNodes = forecastTypeNodes.filter((node) =>
    node.value ? isForecastTypeAvailableForSite(tree, siteId, currentForecastType, node.value) : false,
  );

  return availableForecastTypeNodes.length > 0 ? availableForecastTypeNodes : forecastTypeNodes;
}

export function resolveAvailableEnergyForecastTypeIds(
  tree: AvailableValueNode | undefined,
  siteId?: string,
  currentForecastType?: string,
) {
  return resolveAvailableEnergyForecastTypeNodes(tree, siteId, currentForecastType)
    .map((node) => node.value)
    .filter((value): value is string => !!value);
}

export function useAvailableEnergyForecastTypeAvailability(siteId?: string, currentForecastType?: string) {
  const { tree } = useAvailableEnergyForecastDataTree();

  const availableForecastTypeNodes = useMemo(
    () => resolveAvailableEnergyForecastTypeNodes(tree, siteId, currentForecastType),
    [tree, siteId, currentForecastType],
  );
  const availableForecastTypeIds = useMemo(
    () => availableForecastTypeNodes.map((node) => node.value).filter((value): value is string => !!value),
    [availableForecastTypeNodes],
  );
  const availableForecastTypeIdSet = useMemo(() => new Set(availableForecastTypeIds), [availableForecastTypeIds]);
  const isForecastTypeAvailable = (forecastType: string) =>
    availableForecastTypeIds.length === 0 || availableForecastTypeIdSet.has(forecastType);

  return {
    availableForecastTypeNodes,
    availableForecastTypeIds,
    isForecastTypeAvailable,
  };
}

type AvailableEnergyForecastConfigProps = {
  energyPlot: EnergyPlot;
  onChange: (energyPlot: EnergyPlot) => void;
  onTypeChange: (id: number, forecastType: string) => void;
  onUnitChange: (value: string) => void;
};

type EnergyForecastDropdownFieldProps = {
  activeId: string[];
  items: DropdownItem[];
  itemSelectedHandler: (selectedItems: DropdownItem[]) => void;
  disabled?: boolean;
};

type EnergyForecastSubSitesFieldProps =
  | {
      type: "checkboxes";
      items: AvailableValueNode[];
      isChecked: (nodeValue: string | undefined) => boolean;
      onToggle: (value: string, checked: boolean) => void;
    }
  | {
      type: "radio";
      items: RadioItem[];
      selected: string;
      onToggle: (value: string) => void;
    };

/**
 * Builds the derived config and event handlers for the energy forecast form.
 *
 * This hook stays plot-level only:
 * - it exposes available models for the current forecast type
 * - it handles forecast/country/sub-site selection
 * - it does not manage group model state or ensemble member state
 */
export function useAvailableEnergyForecastConfig({
  energyPlot,
  onChange,
  onTypeChange,
  onUnitChange,
}: AvailableEnergyForecastConfigProps) {
  const { tree } = useAvailableEnergyForecastDataTree();
  const isLoading = useEnergyForecastIsLoading();
  const availableForecastTypeNodes = useMemo(
    () => resolveAvailableEnergyForecastTypeNodes(tree, energyPlot.siteId, energyPlot.forecastType),
    [tree, energyPlot.siteId, energyPlot.forecastType],
  );
  const availableForecastTypeIds = useMemo(
    () => availableForecastTypeNodes.map((node) => node.value).filter((value): value is string => !!value),
    [availableForecastTypeNodes],
  );
  const availableForecastTypeIdSet = useMemo(() => new Set(availableForecastTypeIds), [availableForecastTypeIds]);
  const resolvedForecastType =
    availableForecastTypeIds.includes(energyPlot.forecastType) || availableForecastTypeIds.length === 0
      ? energyPlot.forecastType
      : availableForecastTypeIds[0];

  /**
   * Returns the best matching tree node for a given value.
   *
   * It first tries an exact match, then a regex-based fallback, and finally
   * falls back to the first available child node.
   */

  const forecastTypeNode = useMemo(
    () => tree?.findChildrenNode(energyPlot.forecastType),
    [tree, energyPlot.forecastType],
  );

  const availableModelNodes = useMemo(() => {
    const modelNodes = forecastTypeNode?.children || [];
    const selectedSiteId = energyPlot.siteId;
    const forecastType = energyPlot.forecastType;

    if (!selectedSiteId) {
      return modelNodes;
    }

    const canonicalSelectedSiteId = toCanonicalSiteId(selectedSiteId, forecastType);
    const modelsForSelectedSite = modelNodes.filter((modelNode) =>
      (modelNode.children || []).some(
        (siteNode) => siteNode.value && toCanonicalSiteId(siteNode.value, forecastType) === canonicalSelectedSiteId,
      ),
    );

    return modelsForSelectedSite.length > 0 ? modelsForSelectedSite : modelNodes;
  }, [forecastTypeNode, energyPlot.siteId, energyPlot.forecastType]);

  const availableSiteNodes = useMemo(() => {
    const sitesByCanonicalId = new Map<string, AvailableValueNode[]>();
    const forecastType = energyPlot.forecastType;

    for (const modelNode of forecastTypeNode?.children || []) {
      for (const siteNode of modelNode.children || []) {
        if (siteNode.value) {
          const canonicalSiteId = toCanonicalSiteId(siteNode.value, forecastType);
          const currentNodes = sitesByCanonicalId.get(canonicalSiteId) || [];
          if (!currentNodes.some((node) => node.value === siteNode.value)) {
            sitesByCanonicalId.set(canonicalSiteId, [...currentNodes, siteNode]);
          }
        }
      }
    }

    return [...sitesByCanonicalId.values()]
      .map((nodes) => {
        const normalizedNode = nodes.find(
          (node) => node.value && toCanonicalSiteId(node.value, forecastType) === node.value,
        );
        return normalizedNode || nodes[0];
      })
      .filter((node): node is AvailableValueNode => !!node);
  }, [forecastTypeNode, energyPlot.forecastType]);

  const availableSubSitesNodes = useMemo(() => {
    const selectedSiteId = energyPlot.siteId;
    const siteNode = forecastTypeNode?.children
      ?.flatMap((modelNode) => modelNode.children || [])
      ?.find((candidate) => candidate.value === selectedSiteId);
    return siteNode?.children || [];
  }, [forecastTypeNode, energyPlot.siteId, energyPlot.forecastType]);
  const handleForecastTypeSelection = (selectedItems: DropdownItem[]) => {
    const selectedItem = selectedItems[0];

    if (
      selectedItem?.id &&
      selectedItem.id !== energyPlot.forecastType &&
      availableForecastTypeIdSet.has(selectedItem.id)
    ) {
      onTypeChange(energyPlot.id, selectedItem.id);
    }
  };

  const handleUnitSelection = (selectedItems: DropdownItem[]) => {
    const selectedItem = selectedItems[0];

    if (selectedItem?.id && energyPlot.valueUnit !== selectedItem.id) {
      onUnitChange(selectedItem.id);
    }
  };

  const handleSiteIdSelection = (selectedItems: DropdownItem[]) => {
    const selectedItem = selectedItems[0];
    if (selectedItem?.id) {
      const currentForecastTypeIds = resolveAvailableEnergyForecastTypeIds(
        tree,
        selectedItem.id,
        energyPlot.forecastType,
      );

      onChange({
        ...energyPlot,
        siteId: selectedItem.id,
        subSiteIds: [selectedItem.id],
      });

      if (currentForecastTypeIds.length > 0 && !currentForecastTypeIds.includes(energyPlot.forecastType)) {
        onTypeChange(energyPlot.id, currentForecastTypeIds[0]);
      }
    }
  };

  const isSubSiteChecked = (nodeValue: string | undefined): boolean => {
    return isEnergySubSiteChecked(energyPlot, nodeValue);
  };

  const forecastFieldProps: EnergyForecastDropdownFieldProps = {
    activeId: [resolvedForecastType || energyPlot.forecastType],
    items: availableForecastTypeNodes.map((node) => transformNodeToDropdownItemForForecastType(node)),
    itemSelectedHandler: handleForecastTypeSelection,
    disabled: availableForecastTypeIds.length === 1,
  };

  const unitOptions = [
    {
      id: "GW",
      title: "GW",
    },
    {
      id: "MW",
      title: "MW",
    },
  ];
  const unitFieldProps: EnergyForecastDropdownFieldProps = {
    activeId: [energyPlot.valueUnit ?? ENERGY_FORECAST_VALUE_UNIT],
    items: unitOptions,
    itemSelectedHandler: handleUnitSelection,
  };

  const activeSiteId = useMemo(() => {
    const forecastType = energyPlot.forecastType;
    const canonicalSelectedSiteId = toCanonicalSiteId(energyPlot.siteId, forecastType);
    return (
      availableSiteNodes.find(
        (node) => node.value && toCanonicalSiteId(node.value, forecastType) === canonicalSelectedSiteId,
      )?.value || energyPlot.siteId
    );
  }, [availableSiteNodes, energyPlot.siteId, energyPlot.forecastType]);

  const countryFieldProps: EnergyForecastDropdownFieldProps = {
    activeId: [activeSiteId],
    items: availableSiteNodes.map((node) => transformNodeToDropdownItem(node)),
    itemSelectedHandler: handleSiteIdSelection,
  };

  const subSitesFieldProps: EnergyForecastSubSitesFieldProps | null =
    availableSubSitesNodes.length > 0
      ? {
          type: "checkboxes",
          items: availableSubSitesNodes,
          isChecked: isSubSiteChecked,
          onToggle: (value: string, checked: boolean) => {
            onChange({
              ...energyPlot,
              subSiteIds: updateEnergySubSiteIds(energyPlot, value, checked),
            });
          },
        }
      : null;

  return {
    tree,
    isLoading,
    availableModelNodes,
    forecastFieldProps,
    countryFieldProps,
    subSitesFieldProps,
    unitFieldProps,
  };
}
